// app/features/templates/services_and_hooks/useTemplatesQueryHook.ts
"use client";

import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';

import { FetchTemplatesParams, FetchTemplatesResponse, FileDownloadInfo, Template, TemplateCategory } from '../templates.interface';
import { templatesService } from './templatesService';

// --- Infinite Query for Template Listing ---
interface UseGetTemplatesQueryOptions {
  filters: FetchTemplatesParams;
}

export const useGetTemplatesQuery = ({ filters }: UseGetTemplatesQueryOptions) => {
  const limit = filters.limit || 10;

  const queryResult = useInfiniteQuery<FetchTemplatesResponse, Error>({
    queryKey: ['templates', filters],
    queryFn: ({ pageParam = 1 }) => {
      const page = typeof pageParam === 'number' ? pageParam : 1;
      return templatesService.fetchTemplates({ ...filters, page });
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.data || lastPage.data.length === 0) {
        return undefined;
      }
      const fetchedItemsCount = allPages.reduce((acc, page) => acc + page.data.length, 0);
      if (lastPage.count !== null && fetchedItemsCount >= lastPage.count) {
        return undefined;
      }
      if (lastPage.data.length < limit) {
        return undefined;
      }
      return allPages.length + 1;
    },
  });

  const templates = queryResult.data?.pages.flatMap(page => page.data) ?? [];
  const totalCount = queryResult.data?.pages?.[0]?.count ?? 0;
  
  // Debug logging to understand the count issue
  if (typeof window !== 'undefined' && queryResult.data?.pages?.[0]) {
    console.log('[useTemplatesQuery] Debug info:', {
      filters,
      pagesCount: queryResult.data.pages.length,
      firstPageCount: queryResult.data.pages[0].count,
      firstPageDataLength: queryResult.data.pages[0].data?.length,
      templatesLength: templates.length,
      totalCount
    });
  }

  return {
    ...queryResult,
    templates,
    totalCount,
  };
};

// --- Query for Single Template by Slug ---
export const useGetTemplateBySlugQuery = (slug: string | null) => {
  return useQuery<Template, Error>({
    queryKey: ['template', slug],
    queryFn: () => {
      if (!slug) throw new Error("Slug is required for useGetTemplateBySlugQuery.");
      return templatesService.fetchTemplateBySlug(slug);
    },
    enabled: !!slug,
  });
};

// --- Query for Active Template Categories ---
export const useGetActiveCategoriesQuery = () => {
  return useQuery<TemplateCategory[], Error>({
    queryKey: ['template-categories-active'],
    queryFn: () => templatesService.fetchActiveCategories(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// --- [REPLACED] Mutation for File Download ---
// The old `useGetTemplateDownloadUrlMutation` is GONE. This is its replacement.
export const useDownloadFileMutation = () => {
  return useMutation<FileDownloadInfo, Error, string>({ // <SuccessType, ErrorType, VariablesType>
    mutationFn: (fileId: string) => {
      // Calls the new, correct service function.
      return templatesService.downloadFile(fileId);
    },
    onSuccess: async (data) => {
      // The mutation itself handles the download logic, keeping component hooks clean.
      toast.success(`Downloading "${data.fileName}"...`);
      
      try {
        // Fetch the file content from the signed URL
        const response = await fetch(data.downloadUrl);
        if (!response.ok) {
          throw new Error('Failed to fetch file');
        }
        
        // Create a blob from the response
        const blob = await response.blob();
        
        // Create a blob URL
        const blobUrl = window.URL.createObjectURL(blob);
        
        // Create and click download link
        const link = document.createElement('a');
        link.href = blobUrl;
        link.setAttribute('download', data.fileName);
        document.body.appendChild(link);
        link.click();
        
        // Cleanup
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
        
      } catch (fetchError) {
        console.error('Error downloading file:', fetchError);
        toast.error('Failed to download file. Please try again.');
      }
    },
    onError: (error) => {
      // Handles all errors from the download attempt, including permission errors.
      console.error("Download failed:", error.message);
      toast.error(error.message || 'Download failed. Please try again.');
    },
  });
};
